// Pillar D — post-Customer-Merge duplicate-Patient-group inline merge.
// Reuses the same mergePatient callable + PatientMergeColumn (Pillar C) the
// standalone Patient Merge flow uses, scoped down to exactly the patients in
// one duplicate group (no search/add — the group membership is fixed, it
// came back from the merge_customers RPC's get_duplicate_patient_groups).
import { useEffect, useMemo, useState, useCallback } from "react";
import { useIntl } from "react-intl";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import getPatientForMerge from "../../Patient/MergePatients/getPatientForMerge";
import { getConflictingAppointmentIds } from "../../Patient/MergePatients/checkPatientMergeBlockers";
import {
  PatientInMerge,
  PatientFieldSelections,
  PatientMergeFieldKey,
} from "../../Patient/MergePatients/types";
import patientMergeMessages from "../../Patient/MergePatients/messages";
import { DuplicatePatientGroupOutcome, MergeResult } from "./types";

export type DuplicatePatientGroup =
  MergeResult["duplicatePatientGroups"][number];

// Mirrors PatientMergeColumn's FIELD_ROWS minus "customer" — every patient in
// a duplicate group already belongs to the same (just-merged) customer, so
// that field has no ambiguity to resolve.
const FIELD_KEYS: PatientMergeFieldKey[] = [
  "mrn",
  "firstName",
  "lastName",
  "dateBirth",
  "address",
  "race",
  "ethnicity",
  "sex",
];

const emptySelections = (): PatientFieldSelections =>
  Object.fromEntries(
    FIELD_KEYS.map((k) => [k, null]),
  ) as PatientFieldSelections;

const getFieldValue = (
  patient: PatientInMerge,
  field: PatientMergeFieldKey,
): string | null => {
  switch (field) {
    case "mrn":
      return patient.mrn || null;
    case "firstName":
      return patient.firstName || null;
    case "lastName":
      return patient.lastName || null;
    case "dateBirth":
      return patient.dateBirth;
    case "address":
      return [
        patient.city,
        patient.state,
        patient.postalCode,
        patient.country,
      ].some(Boolean)
        ? "has-value"
        : null;
    case "race":
      return patient.race;
    case "ethnicity":
      return patient.ethnicity;
    case "sex":
      return patient.sex;
    default:
      return null;
  }
};

const groupKeyOf = (group: DuplicatePatientGroup): string =>
  group.patientIds.join("-");

const useDuplicatePatientGroups = (
  duplicatePatientGroups: DuplicatePatientGroup[],
) => {
  const mergePatient = useFirebaseAppFunction("mergePatient");
  const { formatMessage } = useIntl();

  // key -> fetched patients, or undefined while loading.
  const [groupPatients, setGroupPatients] = useState<
    Record<string, PatientInMerge[] | undefined>
  >({});
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [fieldSelections, setFieldSelections] =
    useState<PatientFieldSelections>(emptySelections());
  const [relationship, setRelationship] = useState<"ward" | "family" | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [outcomes, setOutcomes] = useState<
    Record<string, DuplicatePatientGroupOutcome | undefined>
  >({});

  // Fetch every group's patients up front — needed even for collapsed cards,
  // since the "Conflict" vs "To merge" state (AC37/38) depends on each
  // group's appointment data, not just the expanded one.
  useEffect(() => {
    duplicatePatientGroups.forEach((group) => {
      const key = groupKeyOf(group);
      Promise.all(group.patientIds.map((id) => getPatientForMerge(id))).then(
        (patients) => {
          const resolved = patients.filter(
            (p): p is PatientInMerge => p !== null,
          );
          setGroupPatients((prev) => ({ ...prev, [key]: resolved }));
        },
      );
    });
    // Groups are a fixed snapshot from one mergeResult — only ever fetch once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expandGroup = useCallback(
    (group: DuplicatePatientGroup) => {
      const key = groupKeyOf(group);
      const patients = groupPatients[key] ?? [];
      setExpandedKey(key);
      setFieldSelections(emptySelections());

      // AC44 — pre-select the relationship if every patient in the group
      // agrees on it; leave unselected (AC45 disables Merge) if mixed.
      const relationships = new Set(
        patients.map((p) => p.linkedCustomer?.relationship ?? null),
      );
      if (relationships.size === 1) {
        const only = Array.from(relationships)[0];
        setRelationship(only === "ward" || only === "family" ? only : null);
      } else {
        setRelationship(null);
      }
    },
    [groupPatients],
  );

  const collapseGroup = useCallback(() => {
    setExpandedKey(null);
    setFieldSelections(emptySelections());
    setRelationship(null);
  }, []);

  const setFieldSelection = useCallback(
    (field: PatientMergeFieldKey, patientId: string) => {
      setFieldSelections((prev) => ({ ...prev, [field]: patientId }));
    },
    [],
  );

  const canSubmitExpanded = (patients: PatientInMerge[]): boolean =>
    relationship !== null &&
    FIELD_KEYS.every((field) => {
      const anyHasValue = patients.some(
        (p) => getFieldValue(p, field) !== null,
      );
      if (!anyHasValue) return true;
      const selectedId = fieldSelections[field];
      if (!selectedId) return false;
      const patient = patients.find((p) => p.id === selectedId);
      return !!patient && getFieldValue(patient, field) !== null;
    });

  const submitGroup = useCallback(
    async (group: DuplicatePatientGroup, selectedCustomerId: string) => {
      const key = groupKeyOf(group);
      const patients = groupPatients[key] ?? [];
      const selectedPatientId = fieldSelections.mrn;
      const selectedPatient = patients.find((p) => p.id === selectedPatientId);
      if (!selectedPatientId || !selectedPatient) return;

      const nonSelectedPatientIds = patients
        .filter((p) => p.id !== selectedPatientId)
        .map((p) => p.id);
      const addressOwner = fieldSelections.address
        ? patients.find((p) => p.id === fieldSelections.address)
        : null;

      setIsSubmitting(true);
      try {
        const { data: response } = await mergePatient({
          selectedPatientId,
          nonSelectedPatientIds,
          firstName:
            (fieldSelections.firstName &&
              patients.find((p) => p.id === fieldSelections.firstName)
                ?.firstName) ||
            selectedPatient.firstName,
          lastName:
            (fieldSelections.lastName &&
              patients.find((p) => p.id === fieldSelections.lastName)
                ?.lastName) ||
            selectedPatient.lastName,
          dateBirth: ((fieldSelections.dateBirth &&
            patients.find((p) => p.id === fieldSelections.dateBirth)
              ?.dateBirth) ||
            selectedPatient.dateBirth) as string,
          city: addressOwner?.city ?? null,
          state: addressOwner?.state ?? null,
          postalCode: addressOwner?.postalCode ?? null,
          country: addressOwner?.country ?? null,
          race:
            (fieldSelections.race &&
              patients.find((p) => p.id === fieldSelections.race)?.race) ??
            null,
          ethnicity:
            (fieldSelections.ethnicity &&
              patients.find((p) => p.id === fieldSelections.ethnicity)
                ?.ethnicity) ??
            null,
          sex:
            (fieldSelections.sex &&
              patients.find((p) => p.id === fieldSelections.sex)?.sex) ??
            null,
          customerId: selectedCustomerId,
          isWard: relationship === "ward",
          isFamilyMember: relationship === "family",
        });

        if (response.status === "error") {
          const errorMap: Record<string, string> = {
            CHECKOUT_APPOINTMENT_EXISTS: formatMessage(
              patientMergeMessages.errorCheckoutAppointment,
            ),
            CONFLICTING_APPOINTMENTS: formatMessage(
              patientMergeMessages.errorConflictingAppointments,
            ),
            RECORD_LOCKED: formatMessage(
              patientMergeMessages.errorRecordLocked,
            ),
            PACS_MERGE_FAILED: formatMessage(
              patientMergeMessages.errorPacsMergeFailed,
              {
                name: `${selectedPatient.firstName} ${selectedPatient.lastName}`,
              },
            ),
          };
          setOutcomes((prev) => ({
            ...prev,
            [key]: {
              status: "error",
              title:
                errorMap[response.error.code] ??
                formatMessage(patientMergeMessages.errorGeneric),
            },
          }));
        } else {
          setOutcomes((prev) => ({
            ...prev,
            [key]: {
              status: "success",
              patientName: `${selectedPatient.firstName} ${selectedPatient.lastName}`,
              patientMrn: selectedPatient.mrn,
            },
          }));
        }
      } catch {
        setOutcomes((prev) => ({
          ...prev,
          [key]: {
            status: "error",
            title: formatMessage(patientMergeMessages.errorGeneric),
          },
        }));
      } finally {
        setIsSubmitting(false);
        collapseGroup();
      }
    },
    [
      groupPatients,
      fieldSelections,
      relationship,
      mergePatient,
      formatMessage,
      collapseGroup,
    ],
  );

  const groups = useMemo(
    () =>
      duplicatePatientGroups.map((group) => {
        const key = groupKeyOf(group);
        const patients = groupPatients[key];
        const conflictingAppointmentIds = patients
          ? getConflictingAppointmentIds(patients)
          : [];
        return {
          group,
          key,
          patients,
          isLoading: patients === undefined,
          hasConflict: conflictingAppointmentIds.length > 0,
          conflictingAppointmentIds,
          isExpanded: expandedKey === key,
          outcome: outcomes[key],
        };
      }),
    [duplicatePatientGroups, groupPatients, expandedKey, outcomes],
  );

  return {
    groups,
    fieldSelections,
    relationship,
    setRelationship,
    setFieldSelection,
    isSubmitting,
    isAnyExpanded: expandedKey !== null,
    expandGroup,
    collapseGroup,
    submitGroup,
    canSubmitExpanded,
    FIELD_KEYS,
  };
};

export default useDuplicatePatientGroups;
